import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import './ConfirmModal.css';

const ICON_MAP = {
    danger: ExclamationTriangleIcon,
    info:   InformationCircleIcon,
    default: InformationCircleIcon,
};

/**
 * Themed blocking confirmation modal (v5 design).
 * Use for reversible/destructive actions that require an explicit user response.
 * Non-blocking informational messages should use toastInfo instead.
 */
export default function ConfirmModal({
    open,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel  = 'Cancel',
    variant      = 'default',   // 'default' | 'danger' | 'info'
    onConfirm,
    onCancel,
    busy         = false,        // disables confirm + cancel while async work runs
}) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !busy) onCancel?.();
            if (e.key === 'Enter'  && !busy) onConfirm?.();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, busy, onCancel, onConfirm]);

    if (!open) return null;

    const Icon = ICON_MAP[variant] ?? ICON_MAP.default;
    const variantClass = `parkpro-confirm-${variant}`;

    return createPortal(
        <div
            className="parkpro-confirm-overlay"
            role="presentation"
            onClick={(e) => {
                if (e.target === e.currentTarget && !busy) onCancel?.();
            }}
        >
            <div
                className={`parkpro-confirm-modal ${variantClass}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="parkpro-confirm-title"
            >
                <div className="parkpro-confirm-icon-wrap" aria-hidden="true">
                    <Icon className="parkpro-confirm-icon" />
                </div>
                {title && (
                    <h2 id="parkpro-confirm-title" className="parkpro-confirm-title">
                        {title}
                    </h2>
                )}
                {message && (
                    <p className="parkpro-confirm-msg">{message}</p>
                )}
                <div className="parkpro-confirm-actions">
                    <button
                        type="button"
                        className="parkpro-confirm-btn parkpro-confirm-btn-cancel"
                        disabled={busy}
                        onClick={onCancel}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className={`parkpro-confirm-btn parkpro-confirm-btn-confirm ${variantClass}`}
                        disabled={busy}
                        onClick={onConfirm}
                    >
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
